import createApiClient from "./api";

const api = createApiClient();

export const fetchSubmittals = async (
  projectId,
  page = 1,
  pageSize = 10,
  filters,
  masterSearch = "",
  sortField = null,
  sortOrder = "asc",
  isExportPdf = false,
  includeMostRecentResponseDate = false,
  archivedFilter = "active"
) => {
  const token = localStorage.getItem("token");
  const response = await api.get(`/submittals`, {
    params: {
      project_id: projectId,
      skip: (page - 1) * pageSize,
      limit: pageSize,
      filters: JSON.stringify(filters),
      master_search: masterSearch,
      sort_field: sortField,
      sort_order: sortOrder,
      is_export_pdf: isExportPdf,
      include_most_recent_response_date: includeMostRecentResponseDate,
      archived_filter: archivedFilter,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const { data, total } = response.data;
  const submittals = data.map((element) => ({
    ...element,
    type: element.type || null,
    edms: element.edms || null,
    review_status: element.review_status || null,
    formatted_number: !!element.specification_section_details?.section_number
      ? `${element.specification_section_details.section_number}-${element.number}`
      : element.number,
    formatted_ball_in_courts: element?.ball_in_court_details
      .map((u) => (u.vendor_name ? `${u.username} (${u.vendor_name})` : u.username))
      .join(", "),
    formatted_received_from: element.received_from_details?.username,
    formatted_spec_section: !!element.specification_section_details
      ? element.specification_section_details?.title
      : "N/A",
  }));

  return { submittals, total };
};

export const fetchSubmittalsSimple = async (projectId) => {
  const token = localStorage.getItem("token");
  const response = await api.get(`/submittals/list-submittals-basic`, {
    params: {
      project_id: projectId,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return { submittals: response.data.data || [] };
};

export const createSubmittal = async (
  submittalData,
  coversheetId = null,
  sendEmail = false
) => {
  const token = localStorage.getItem("token");
  const response = await api.post("/submittals", submittalData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      ...(coversheetId ? { coversheet_id: coversheetId } : {}),
      email_notification: sendEmail,
    },
  });
  return response.data;
};

export const fetchNextSubmittalNumber = async (
  projecId,
  specSectionId = null
) => {
  const token = localStorage.getItem("token");
  const response = await api.get(`/submittals/next-number`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: specSectionId
      ? { spec_section_id: specSectionId, project_id: projecId }
      : { project_id: projecId },
  });
  return response.data;
};

export const updateSubmittal = async (
  submittalId,
  submittalData,
  sendEmail = true,
  coversheetId = null
) => {
  const token = localStorage.getItem("token");
  const response = await api.put(`/submittals/${submittalId}`, submittalData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      ...(coversheetId ? { coversheet_id: coversheetId } : {}),
      email_notification: sendEmail,
    },
  });
  return response.data;
};

export const archiveSubmittal = async (submittalId, archived) => {
  const token = localStorage.getItem("token");
  const response = await api.patch(
    `/submittals/${submittalId}/archive`,
    { archived },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

export const deleteSubmittal = async (submittalId) => {
  const token = localStorage.getItem("token");
  const response = await api.delete(`/submittals/${submittalId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const createSubmittalCoversheet = async (submittalData, submittalId = null) => {
  const token = localStorage.getItem("token");
  const now = new Date();
  const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  
  const response = await api.post(
    "/submittals/coversheet",
    {
      ...submittalData,
      local_date: localDate,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        ...(submittalId ? { submittal_id: submittalId } : {}),
      },
    }
  );
  return response.data;
};


export const downloadSubmittalAttachments = async (submittalId, downloadType = "zip") => {
  const token = localStorage.getItem("token");
  const isPdfReport = downloadType === "pdf";
  const url = `/submittals/${submittalId}/download-attachments`;
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    ...(!isPdfReport && {
      params: { downloadType },
      responseType: "blob",
    }),
  };
  const response = isPdfReport
    ? await api.post(url, null, config)
    : await api.get(url, config);
  return response.data;
};

export const getSubmittalById = async (submittalId) => {
  const token = localStorage.getItem("token");

  try {
    const response = await api.get(`/submittals/${submittalId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  return response.data;
  } catch (error) {
    console.error("Error fetching submittal", error);
    throw new Error("Failed to fetch submittal");
  }
};

export const getSubmittalDefaultNotifyRecipients = async (submittalId) => {
  const response = await api.get(`/submittals/${submittalId}/default-notify-recipients`, {
    headers: {},
  });
  return response.data?.user_ids || [];
};

export const notifyProjectUsersAboutSubmittal = async (submittalId, userIds = null) => {
  const requestBody = {};
  if (userIds && Array.isArray(userIds) && userIds.length > 0) {
    requestBody.user_ids = userIds;
  }

  const response = await api.post(
    `/submittals/${submittalId}/notify`,
    requestBody,
    { headers: {} }
  );
  return response.data;
};
